import React from 'react'
import PropTypes from 'prop-types'
import { gql, useQuery } from '@apollo/client'
import { DateTime } from 'luxon'
import { ThemeProvider } from '@material-ui/core/styles'
import CircularProgress from '@material-ui/core/CircularProgress'
import themes, { extendTheme } from 'themes'
import { useAuth } from 'lib/auth'
import SchedulePageBackground from 'pages/schedule/components/page-background'
import PrivateLessonConfirmation from './confirmation'
import { PAGE_BACKGROUND } from './styles'
import { getBookingEmail, pickFacility } from './utils'

const refreshTheme = extendTheme(themes.offeringsRefresh)

const Themed = ({ children }) => (
  <ThemeProvider theme={refreshTheme}>
    <SchedulePageBackground color={PAGE_BACKGROUND}>
      {children}
    </SchedulePageBackground>
  </ThemeProvider>
)

Themed.propTypes = { children: PropTypes.node }

const studentFullName = student =>
  [student?.firstName, student?.lastName].filter(Boolean).join(' ')

// Confirmation shown inline at the end of step 3 when no Stripe return page is
// involved (a free lesson, pay at location, or a coach booking for a student).
// The lesson type came from the step 3 query, which already has the facility
// timezone and the coach's cancellation fields.
export const InlinePrivateLessonConfirmation = ({
  lessonType,
  student,
  guest,
  locationId,
  startDateTime,
  showTotal,
  coachIsViewingOwnSchedule,
  onViewOfferings,
  onBookAnotherSession,
}) => {
  const { user } = useAuth()

  return (
    <Themed>
      <PrivateLessonConfirmation
        coach={lessonType.coach}
        lessonTitle={lessonType.title}
        durationInMinutes={lessonType.durationInMinutes}
        facility={pickFacility(lessonType.facilities, locationId)}
        startDateTime={startDateTime}
        studentName={studentFullName(student)}
        email={getBookingEmail({
          student,
          guest,
          user,
          coachBooksOnBehalfOfStudent: coachIsViewingOwnSchedule,
        })}
        payment={showTotal ? { amountInCents: lessonType.priceInCents } : null}
        coachIsViewingOwnSchedule={coachIsViewingOwnSchedule}
        onViewOfferings={onViewOfferings}
        onBookAnotherSession={onBookAnotherSession}
      />
    </Themed>
  )
}

InlinePrivateLessonConfirmation.propTypes = {
  lessonType: PropTypes.object.isRequired,
  student: PropTypes.object.isRequired,
  guest: PropTypes.object,
  locationId: PropTypes.string,
  startDateTime: PropTypes.string,
  showTotal: PropTypes.bool,
  coachIsViewingOwnSchedule: PropTypes.bool,
  onViewOfferings: PropTypes.func.isRequired,
  onBookAnotherSession: PropTypes.func.isRequired,
}

// The credit step's lesson type query does not select the facility timezone or
// the coach's cancellation fields (and adding them there would change the
// timezone its analytics use), so the credit confirmation loads them here.
export const CREDIT_CONFIRMATION_DETAILS = gql`
  query CreditConfirmationDetails($id: ID!) {
    lessonTypes(id: $id, includeDeleted: true) {
      id
      facilities {
        id
        timezone
      }
      coach {
        canStudentCancel
        cancellationPolicy
      }
    }
  }
`

// Confirmation after redeeming a lesson pack credit. `remaining` is the
// balance after this booking.
export const CreditLessonConfirmation = ({
  lessonType,
  student,
  locationId,
  startDateTime,
  remaining,
  onViewOfferings,
  onBookAnotherSession,
}) => {
  const { user } = useAuth()
  const { data, loading } = useQuery(CREDIT_CONFIRMATION_DETAILS, {
    variables: { id: lessonType.id },
  })

  if (loading) {
    return (
      <Themed>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 96 }}>
          <CircularProgress />
        </div>
      </Themed>
    )
  }

  const details = data?.lessonTypes?.[0]
  const facility = pickFacility(lessonType.facilities, locationId)
  const detailsFacility = pickFacility(details?.facilities, locationId)
  // The credit step does not select the timezone, so fall back to the browser
  // zone when the details lookup fails; the time still prints with a zone label.
  const timezone = detailsFacility?.timezone || DateTime.local().zoneName

  return (
    <Themed>
      <PrivateLessonConfirmation
        coach={{ ...lessonType.coach, ...details?.coach }}
        lessonTitle={lessonType.title}
        durationInMinutes={lessonType.durationInMinutes}
        facility={facility && { ...facility, ...detailsFacility, timezone }}
        startDateTime={startDateTime}
        studentName={studentFullName(student)}
        email={user?.email}
        credit={{ remaining }}
        onViewOfferings={onViewOfferings}
        onBookAnotherSession={onBookAnotherSession}
      />
    </Themed>
  )
}

CreditLessonConfirmation.propTypes = {
  lessonType: PropTypes.object.isRequired,
  student: PropTypes.object.isRequired,
  locationId: PropTypes.string,
  startDateTime: PropTypes.string,
  remaining: PropTypes.number.isRequired,
  onViewOfferings: PropTypes.func.isRequired,
  onBookAnotherSession: PropTypes.func.isRequired,
}
